import { Label } from "@/shared/ui/label";
import { Input } from "@/shared/ui/input";
import { Separator } from "@/shared/ui/separator";
import { UnifiedUpload } from "@/shared/ui/UnifiedUpload";
import { toast } from "@/shared/hooks/use-toast";
import { CustomSwitch } from "@/shared/widgets/switch";
import { CheckIcon, XIcon } from "lucide-react";
import { ReactSelect } from "@/shared/widgets";
import { useCustomQuery } from "@/shared/hooks/useCustomQuery";
import { useCustomMutation } from "@/shared/hooks/useCustomMutation";
import { TagsService, Tag, LanguageCode } from "@/features/tags/service/tagService";
import { useCategoriesData } from "@/features/categories/hooks/useCategoriesData";
import { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "@/shared/i18n";
import { TranslationsField, type TranslationItem } from "@/shared/widgets/translations-field";

interface PresetTag {
  id?: string;
  tagId: string;
  presetId?: string;
  deleted?: boolean;
  tag: Tag;
}

interface PresetBasicInfoProps {
  presetId: string | null;
  isEditing: boolean;
  name: string;
  tags: string;
  setTags: (tags: string) => void;
  presetsTags?: PresetTag[];
  onPresetsTagsChange?: (presetsTags: PresetTag[]) => void;
  translations: TranslationItem[];
  onTranslationsChange: (translations: TranslationItem[]) => void;
  coverPreview: string | null;
  lutFilename: string | null;
  lutPreview?: string | null;
  published: boolean;
  nameError: boolean;
  tagsError: boolean;
  coverError: boolean;
  lutError: boolean;
  translationsError?: string;
  isFree: boolean;
  handleIsFreeChange: (checked: boolean) => void;
  handleNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleTagsChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePublishedChange: (checked: boolean) => void;
  handleCoverChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleLutChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveCover: () => void;
  onRemoveLut: () => void;
  selectedCategoryIds: string[];
  setSelectedCategoryIds: (categoryIds: string[]) => void;
  categoryError?: boolean;
}

export const PresetBasicInfo = ({
  presetId,
  isEditing,
  name,
  tags,
  setTags,
  presetsTags,
  onPresetsTagsChange,
  translations,
  onTranslationsChange,
  coverPreview,
  lutFilename,
  lutPreview = null,
  published,
  nameError,
  tagsError,
  coverError,
  lutError,
  translationsError,
  isFree,
  handleIsFreeChange,
  handleNameChange,
  handleTagsChange,
  handlePublishedChange,
  handleCoverChange,
  handleLutChange,
  onRemoveCover,
  onRemoveLut,
  selectedCategoryIds,
  setSelectedCategoryIds,
  categoryError = false,
}: PresetBasicInfoProps) => {
  const { t } = useTranslation('presets');

  const [selectedTags, setSelectedTags] = useState<{ value: string; label: string; }[]>([]);
  const [tagSearchQuery, setTagSearchQuery] = useState<string>("");
  const [categorySearchQuery, setCategorySearchQuery] = useState<string>("");

  const { data: categoriesResponse, isLoading: isLoadingCategories } = useCategoriesData({
    type: "PRESET",
    limit: 9999
  });

  const previousTagsRef = useRef<string[]>([]);

  const { data: tagsResponse, isLoading: isLoadingTags } = useCustomQuery({
    serviceInstance: TagsService.Instance,
    selectAction: (service) => service.getTags,
    parameter: tagSearchQuery.length >= 2 ? { search: tagSearchQuery, limit: 20 } : undefined,
  }) as { data: { data: Tag[]; page: number; limit: number; totalPage: number; totalItems: number } | undefined; isLoading: boolean };

  const linkTagMutation = useCustomMutation({
    serviceInstance: TagsService.Instance,
    selectAction: (service) => service.linkTagToPreset,
  });

  const unlinkTagMutation = useCustomMutation({
    serviceInstance: TagsService.Instance,
    selectAction: (service) => service.unlinkTagFromPreset,
  });

  const tagOptions = useMemo(() => {
    const tagsData = tagsResponse?.data || [];
    return tagsData.map((tag: Tag) => ({
      value: tag.id,
      label: tag.translations.find(t => t.languageCode === 'PT_BR')?.name || tag.translations[0]?.name || tag.id,
    }));
  }, [tagsResponse]);

  const categoryOptions = useMemo(() => {
    const categoriesData = categoriesResponse?.data || [];
    const allCategories = categoriesData.map((category) => ({
      value: category.id,
      label: category.name,
    }));

    if (!categorySearchQuery.trim()) {
      return allCategories;
    }

    const searchLower = categorySearchQuery.toLowerCase().trim();
    const filteredCategories = allCategories.filter((category) =>
      category.label.toLowerCase().includes(searchLower)
    );

    const selectedCategories = allCategories.filter((category) =>
      selectedCategoryIds.includes(category.value || '')
    );

    const combined = [...filteredCategories];
    selectedCategories.forEach((selected) => {
      if (!combined.find((cat) => cat.value === selected.value)) {
        combined.push(selected);
      }
    });

    return combined;
  }, [categoriesResponse, categorySearchQuery, selectedCategoryIds]);

  const searchTags = (query: string) => {
    setTagSearchQuery(query);
  };

  const searchCategories = (query: string) => {
    setCategorySearchQuery(query);
  };

  const handleTagsSelectChange = async (newValue: string | string[]) => {
    const newTagValues = Array.isArray(newValue) ? newValue : [];
    const formattedTags = newTagValues.map(value => ({
      value,
      label: tagOptions.find(opt => opt.value === value)?.label || value
    }));

    setSelectedTags(formattedTags);
    const tagsString = newTagValues.join(',');
    setTags(tagsString);

    if (!presetId && !isEditing) {
      previousTagsRef.current = newTagValues;
      return;
    }

    if (!presetId) {
      console.warn('Preset ID não encontrado para vincular tags');
      return;
    }

    const currentTagValues = previousTagsRef.current;
    const addedTags = newTagValues.filter(tagId => !currentTagValues.includes(tagId));
    const removedTags = currentTagValues.filter(tagId => !newTagValues.includes(tagId));

    previousTagsRef.current = newTagValues;

    for (const tagId of addedTags) {
      try {
        await linkTagMutation.mutateAsync({
          tagId,
          presetId: presetId
        });

        if (onPresetsTagsChange && presetsTags) {
          const tagData = tagOptions.find(option => option.value === tagId);
          if (tagData) {
            const newPresetTag: PresetTag = {
              id: `temp-${tagId}-${Date.now()}`,
              tagId: tagId,
              presetId: presetId,
              deleted: false,
              tag: {
                id: tagId,
                published: true,
                isCategory: false,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                translations: [{
                  languageCode: LanguageCode.PT_BR,
                  name: tagData.label
                }]
              }
            };

            const updatedPresetsTags = [...presetsTags, newPresetTag];
            onPresetsTagsChange(updatedPresetsTags);
          }
        }
      } catch (error) {
        console.error(`Erro ao vincular tag ${tagId}:`, error);
      }
    }

    for (const tagId of removedTags) {
      try {
        await unlinkTagMutation.mutateAsync({
          tagId,
          presetId: presetId
        });

        if (onPresetsTagsChange && presetsTags) {
          const updatedPresetsTags = presetsTags.filter(presetTag => presetTag.tagId !== tagId);
          onPresetsTagsChange(updatedPresetsTags);
        }
      } catch (error) {
        console.error(`Erro ao desvincular tag ${tagId}:`, error);
      }
    }
  };

  useEffect(() => {
    if (presetsTags && presetsTags.length > 0) {
      const formattedTags = presetsTags
        .filter(presetTag => !presetTag.deleted && presetTag.tag)
        .map(presetTag => ({
          value: presetTag.tagId,
          label: presetTag.tag?.translations?.find(t => t.languageCode === 'PT_BR')?.name ||
            presetTag.tag?.translations?.[0]?.name ||
            presetTag.tagId
        }));

      setSelectedTags(formattedTags);
      const tagValues = formattedTags.map(tag => tag.value);
      previousTagsRef.current = tagValues;
    } else if (tags) {
      const tagsArray = tags.split(',').map(tag => tag.trim()).filter(Boolean);
      const formattedTags = tagsArray.map(tag => ({
        value: tag,
        label: tag
      }));

      setSelectedTags(formattedTags);
      previousTagsRef.current = tagsArray;
    }
  }, [presetsTags, tags, presetId]);

  return (
    <div>
      <h2 className="text-xl text-white font-semibold mb-2">{t('presetInfo')}</h2>
    
      <Separator className="bg-white/10 mb-6" />

      {isEditing && presetId && (
        <div className="mb-6">
          <Label className="text-white" htmlFor="preset-id">
            {t('presetId')}
          </Label>
          <Input
            id="preset-id"
            value={presetId}
            readOnly
            className="bg-white/[0.03] border-white/[0.06] text-white font-mono"
          />
          <p className="text-xs text-white/60 mt-1">
            {t('presetIdDescription')}
          </p>
        </div>
      )}

      <div className="mb-8">
        <div className="grid grid-cols-1 3x1:grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="grid grid-cols-2 justify-center items-center rounded-xl gap-4 bg-white/5 p-6">
            <UnifiedUpload
              id="cover"
              title={t('cover')}
              preview={coverPreview}
              onFileChange={handleCoverChange}
              onRemove={onRemoveCover}
              error={coverError}
              variant="cover"
              enableDragDrop={true}
              acceptTypes="image/*"
              maxSizeMB={0.5}
              onError={(error) =>
                toast({
                  title: t('error'),
                  description: error,
                  variant: "destructive",
                })
              }
            />
            <UnifiedUpload
              id="lut"
              title={t('lut')}
              preview={lutPreview || lutFilename}
              filename={lutFilename}
              onFileChange={handleLutChange}
              onRemove={onRemoveLut}
              acceptTypes=".cube,.3dl,.lut,.jpg,.jpeg,.png"
              error={lutError}
              variant="lut"
              enableDragDrop={true}
              maxSizeMB={0.5}
              onError={(error) =>
                toast({
                  title: t('error'),
                  description: error,
                  variant: "destructive",
                })
              }
            />
          </div>

          <div className="flex flex-col gap-4">
            <CustomSwitch
              checked={published}
              onChange={handlePublishedChange}
              id="published"
              label={t('published')}
              descriptionOn={t('publishedOn')}
              descriptionOff={t('publishedOff')}
              iconOn={<CheckIcon className="w-5 h-5 text-green-500" />}
              iconOff={<XIcon className="w-5 h-5 text-red-500" />}
              className="bg-white/10"
            />
            <CustomSwitch
              checked={isFree}
              onChange={handleIsFreeChange}
              id="isFree"
              label={t('isFree')}
              descriptionOn={t('isFreeOn')}
              descriptionOff={t('isFreeOff')}
              iconOn={<CheckIcon className="w-5 h-5 text-green-500" />}
              iconOff={<XIcon className="w-5 h-5 text-red-500" />}
              className="bg-white/10"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="space-y-2">
          <Label className="text-white" htmlFor="name">
            {t('name')}
          </Label>
          <Input
            id="name"
            value={name}
            onChange={handleNameChange}
            placeholder={t('namePlaceholder')}
            required
            className={`bg-white/[0.03] border-white/[0.06] text-white ${nameError ? "border-red-500" : ""
              }`}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white" htmlFor="tags">
            {t('tags')}
          </Label>
          <ReactSelect
            options={tagOptions}
            value={selectedTags.map(tag => tag.value)}
            onChange={(newValue) => handleTagsSelectChange(newValue)}
            multiple
            searchable
            onSearch={searchTags}
            loading={isLoadingTags || linkTagMutation.isPending || unlinkTagMutation.isPending}
            placeholder={t('tagsPlaceholder')}
            noOptionsMessage={isLoadingTags ? t('tagsSearching') : t('tagsMinSearch')}
            minSearchLength={2}
            error={tagsError}
            errorMessage={tagsError ? t('tagsError') : undefined}
          />
          <input
            type="hidden"
            name="tags"
            value={selectedTags.map(tag => tag.value).join(',')}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white" htmlFor="category">
            {t('category')}
          </Label>
          <ReactSelect
            options={categoryOptions}
            value={selectedCategoryIds}
            onChange={(newValue) => {
              const categoryIds = Array.isArray(newValue) ? newValue : [];
              setSelectedCategoryIds(categoryIds);
            }}
            multiple={true}
            searchable
            onSearch={searchCategories}
            loading={isLoadingCategories}
            placeholder={t('categoryPlaceholder')}
            noOptionsMessage={isLoadingCategories ? t('categorySearching') : t('categoryNotFound')}
            minSearchLength={0}
            error={categoryError}
            errorMessage={categoryError ? t('categoryRequired') : undefined}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-white" htmlFor="description">
          {t('description')}
        </Label>
        <TranslationsField
          value={translations}
          onChange={onTranslationsChange}
          error={translationsError}
          placeholder={t('descriptionPlaceholder')}
        />
      </div>
    </div>
  );
};
